import React, { useState } from 'react';
const API_BASE_URL = (import.meta.env.VITE_API_BASE_URL || '').replace(/\/+$/, '');
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Copy, RefreshCw, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

interface WebhookPanelProps {
    data: any;
    onChange: (key: string, value: any) => void;
    flowId: string;
}

export const WebhookPanel = ({ data, onChange, flowId }: WebhookPanelProps) => {
    const [listening, setListening] = useState(false);

    // Auto-generate URL
    const webhookUrl = `${API_BASE_URL}/api/webhooks/trigger/${flowId}`;

    const copyUrl = () => {
        navigator.clipboard.writeText(webhookUrl);
        toast.success("Webhook URL copied to clipboard");
    };

    const startListening = async () => {
        setListening(true);
        toast.info("Listening for events... Trigger your webhook now!");

        // Simulate polling
        setTimeout(() => {
            setListening(false);
            onChange('hasSchema', true);
            toast.success("Event received! Schema automatically detected.");
        }, 5000);
    };

    return (
        <div className="space-y-6">
            <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-yellow-600 mt-0.5" />
                    <div>
                        <h4 className="font-semibold text-yellow-900 text-sm">Zero-Config Mode</h4>
                        <p className="text-xs text-yellow-700 mt-1">
                            This node automatically creates an endpoint for your flow.
                            Send any JSON payload to the URL below to trigger this workflow.
                        </p>
                    </div>
                </div>
            </div>

            <div className="space-y-2">
                <Label>Webhook URL</Label>
                <div className="flex gap-2">
                    <Input value={webhookUrl} readOnly className="bg-gray-50 font-mono text-xs" />
                    <Button variant="outline" size="icon" onClick={copyUrl} title="Copy URL">
                        <Copy className="w-4 h-4" />
                    </Button>
                </div>
            </div>

            <div className="border-t pt-4">
                <Label className="mb-2 block">Schema Discovery</Label>
                {data.hasSchema ? (
                    <div className="flex items-center gap-2 text-green-600 bg-green-50 p-3 rounded-md text-sm border border-green-200">
                        <CheckCircle2 className="w-4 h-4" />
                        <span>Schema Detected & Ready</span>
                        <Button variant="ghost" size="sm" className="ml-auto h-6 text-xs" onClick={() => onChange('hasSchema', false)}>
                            Reset
                        </Button>
                    </div>
                ) : (
                    <div className="space-y-3">
                        <p className="text-xs text-gray-500">
                            We need to know what your data looks like. Click "Listen" and then trigger your webhook to auto-generate the schema.
                        </p>
                        <Button
                            className="w-full"
                            variant={listening ? "secondary" : "default"}
                            onClick={startListening}
                            disabled={listening}
                        >
                            {listening ? (
                                <>
                                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                                    Listening for Event...
                                </>
                            ) : (
                                "Listen for Event"
                            )}
                        </Button>
                    </div>
                )}
            </div>

            {/* HTTP Method Config (Advanced) */}
            <div className="space-y-2 opacity-80 hover:opacity-100 transition-opacity">
                <Label className="text-xs text-gray-400 uppercase tracking-wider">Advanced</Label>
                <div className="flex gap-4">
                    <div>
                        <Label className="text-xs">Method</Label>
                        <select
                            className="h-9 w-24 rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
                            value={data.method || 'POST'}
                            onChange={e => onChange('method', e.target.value)}
                        >
                            <option value="POST">POST</option>
                            <option value="GET">GET</option>
                            <option value="PUT">PUT</option>
                        </select>
                    </div>
                </div>
            </div>
        </div>
    );
};
